'use client';

import type { ReactNode } from 'react';
import { BadgeCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { OrganizationAvatar } from './organization-avatar';
import type { OrganizationSummary } from './organization-types';

export type { OrganizationSummary } from './organization-types';

/**
 * The canonical rich organization row: logo (initials fallback), name, a
 * verified check, and a secondary `@handle · Type` line. `trailing` slots a
 * right-edge affordance (an arrow for a navigable list, a check for a
 * selected combobox option). Mirror of `ProfileRow`.
 */
export function OrganizationRow({
  organization,
  trailing,
  className,
}: {
  organization: OrganizationSummary;
  trailing?: ReactNode;
  className?: string;
}) {
  const typeLabel = useOrganizationTypeLabel(organization.organizationType);
  const meta = [
    organization.handle ? `@${organization.handle}` : null,
    typeLabel,
  ].filter(Boolean);

  return (
    <div className={cn('flex min-w-0 flex-1 items-center gap-3', className)}>
      <OrganizationAvatar
        name={organization.name}
        logoUrl={organization.logoUrl}
        className="h-9 w-9 shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-foreground truncate text-sm font-medium">
            {organization.name}
          </span>
          {organization.verified ? (
            <BadgeCheck className="text-primary h-3.5 w-3.5 shrink-0" />
          ) : null}
        </div>
        {meta.length > 0 ? (
          <div className="text-muted-foreground truncate text-xs">
            {meta.join(' · ')}
          </div>
        ) : null}
      </div>
      {trailing}
    </div>
  );
}

/**
 * Localized singular label for an `organization_type` enum value
 * (school / company / course / competition / foundation / brand / service).
 * Falls back to the raw value when a key is missing.
 */
export function useOrganizationTypeLabel(
  organizationType?: string | null,
): string | null {
  const { t } = useTranslation('organizations');
  if (!organizationType) return null;
  return t(`typeSingular.${organizationType}`, {
    defaultValue: organizationType,
  });
}
